<template>
  <div class="amf-form" :style="{ backgroundColor: backColor }">
    <busy-indicator></busy-indicator>
    <div class="header">
      <div class="container-fluid" v-if="title || showHeader">
        <div class="row">
          <div class="col-6">
            <div class="title">{{title}}</div>
            <div>{{subtitle}}</div>
          </div>
          <div class="col-6 drop-down-button" v-show="showHeader">
            <a
              v-for="b in internalHeaderButtons"
              href="#"
              class="btn btn-default ml-4"
              :key="b.id"
              @click.prevent="() => buttonClick(b)"
            >
              <span v-if="b.iconClass && b.iconClass.length && b.iconClass[0]">
                <i :class="['fa', b.iconClass, 'mr-2']" />
              </span>
              <span>{{b.text}}</span>
            </a>
            <amf-dropdown-menu v-if="showHeaderDropdownMenu" style="display:inline" :items="internalMenuItems" />
          </div>
        </div>
      </div>
    </div>
    <div class="body">
      <slot></slot>
    </div>
    <div class="footer" v-if="showFooter">
      <div v-if="error" class="mx-1">
        <error-message/>
      </div>
      <div class="buttons-container">
        <div class="custom-buttons">
          <div v-for="b in internalFooterButtonsLeft" v-bind:key="b.id">
            <amf-button
              :class="['mr-3', b.buttonClass]"
              :onClick="() => buttonClick(b)"
            >{{b.text}}</amf-button>
          </div>
        </div>

        <div class="standard-buttons">
          <div v-for="b in internalFooterButtons" v-bind:key="b.id">
            <amf-button
              :class="['ml-3', b.buttonClass]"
              :onClick="() => buttonClick(b)"
            >{{b.text}}</amf-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import changeTracker from "../utils/change-tracker";
import { v } from "framework";
import uuid from "uuid/v4";
import AmfDropdownMenu from './AmfDropdownMenu.vue';

export default {
  components: {AmfDropdownMenu},
  data() {
    return {
      error: null,
      guid: uuid()
    };
  },
  props: {
    title: String,
    subtitle: String,
    backColor: {
      type: String,
      default: 'white'
    },
    headerButtons: {
      type: [String, Array],
      default: null
    },
    footerButtonsLeft: {
      type: String,
      default: null
    },
    footerButtons: {
      type: String,
      default: null
    },
    menuItems: {
      type: [String, Array],
      default: null
    }
  },
  provide() {
    return {
      closeRows: this.closeRows,
    }
  },
  created() {
  },
  methods: {
    load() {
      this.closeRows();
    },
    closeRows() {
      this.$_closeChildRows(this);
    },
    $_closeChildRows(component) {
      if (component.$children.length == 0) return;

      component.$children.forEach(child => {
        if (child.cancel && child.closeEditor) {
          child.cancel()
        } else {
          this.$_closeChildRows(child)
        }
      });
    },
    addButtons(buttonsDefinition, buttonsArray, validationRequired) {
      // see examples of button modifiers below
      if (!buttonsDefinition) return;
      let definitions = [];
      const isString = _.isString(buttonsDefinition);
      const isArray = _.isArray(buttonsDefinition);

      if (isString) definitions = buttonsDefinition.split("/");
      if (isArray) definitions = buttonsDefinition;

      definitions.forEach((definition, index) => {
        const isDefinitionString = _.isString(definition);
        const isDefinitionObject = _.isObject(definition);

        if (isDefinitionString) buttonsArray.push(convertStringToButton(definition, index, validationRequired));
        if (isDefinitionObject) buttonsArray.push(convertObjectToButton(definition, index, validationRequired));
      });
    },
    async buttonClick(button) {
      this.error = null;

      if (button.id == "cancel" || button.id == "close") {
        changeTracker.clear();
        if (!this.$parent.cancel && !this.$parent.close) {
          this.$router.back();
          return;
        }
      }

      let handler = this.$parent[button.id];

      if (!handler)
        handler = button.onClick;

      if (button.requiresValidation) {
        if (!v.invalidComponent(this)) {
          try {
            var res = await handler();
            changeTracker.clear();
            if (res && res.url) {
              this.$router.push(res.url);
            } else if (res && _.isString(res)) {
              this.$router.push(res);
            }
          } catch (err) {
            this.error = err;
          }
        }
      } else {
        try {
          await handler();
        } catch (err) {
          this.error = err;
        }
      }
    }
  },
  computed: {
    showHeader() {
      return this.internalHeaderButtons.length > 0 || this.showHeaderDropdownMenu;
    },
    showHeaderDropdownMenu() {
      return this.internalMenuItems.length > 0;
    },
    showFooter() {
      return this.internalFooterButtonsLeft.length > 0 || this.internalFooterButtons.length > 0;      
    },
    internalHeaderButtons() {
      let headerButtons = this.headerButtons || this.$parent.headerButtons;
      if (!headerButtons || headerButtons.length == 0) return [];

      let buttons = [];
      this.addButtons(headerButtons, buttons, false);

      return buttons;
    },
    internalMenuItems() {
      return this.menuItems || this.$parent.menuItems || [];
    },
    internalFooterButtonsLeft() {
      let footerButtonsLeft = this.footerButtonsLeft || this.$parent.footerButtonsLeft;
      if (!footerButtonsLeft || footerButtonsLeft.length == 0) return [];

      let buttons = [];
      this.addButtons(footerButtonsLeft, buttons, false);

      return buttons;
    },
    internalFooterButtons() {
      let footerButtons = this.footerButtons || this.$parent.footerButtons;
      if (!footerButtons || footerButtons.length == 0) return [];

      let buttons = [];
      this.addButtons(footerButtons, buttons, true);

      if (buttons.length > 0) {
        buttons[0].buttonClass = "btn-success";
      }

      return buttons;
    }
  },
  watch: {
    '$route.fullPath': {
        handler: function(newValue, oldValue) {
            if(newValue === oldValue) { return; }
            this.closeRows();
        },
        immediate: true
    }
  }
};

const convertStringToButton = (name, index, validationRequired) => {
  var tokens = name.split(".");
  let text = tokens[0];
  let iconClass = [tokens.length > 1 ? "fa-" + tokens[1] : null];
  let id = utils.toCamelCase(utils.removeSpecialCharacters(text || ""));

  return {
    id,
    iconClass,
    text,
    requiresValidation: validationRequired && index == 0
  };
}
const convertObjectToButton = (obj, index, validationRequired) => {
  const {
    id,
    name,
    buttonClass,
    iconClass,
    text,
    requiresValidation,
    group
  } = obj;

  let button = null;

  // Allow base button props to generate based on name or text passed in
  if (!!name) button = convertStringToButton(name, index, validationRequired);
  if (!button) button = convertStringToButton(text, index, validationRequired);

  return {
    ...button,
    ...obj
  }
}

// examples of AmfForm footerButtons:
// Save/Cancel
// Create/Cancel
// Submit/Close
// Terminate.danger/Cancel
// Terminate.danger/Cancel
</script>

<style lang="scss" scoped>
$formBorderColor: darken(white, 30);
$formBorder: 1px $formBorderColor solid;

.dropdown-group {
  font-weight: bold;
  padding: 0.25rem 1.5rem 0.25rem 1rem;
  font-size: small;
}

.amf-form {
  // background-color: white;
  border: 1px solid #eaeaea;
  height: 100%;
  overflow: hidden;
  display: flex;
  position: relative;
  flex-direction: column;
  text-align: left;

  .header {
    .title, .drop-down-button {
      margin: 0.7rem 0;
      font-size: 1.35rem;
    }
    .menu-dropdown-title {
      font-weight: 300;
    }
    .drop-down-button {
      text-align: right;
    }
    button {
      font-size: 0.9rem;
    }
    a {
      font-size: 0.9rem;
    }

  }

  .body {
    overflow-y: auto;
    height: 100%;
    overflow-x: hidden;
  }

  .buttons-container {
    padding: 1rem;
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    .standard-buttons,
    .custom-buttons {
      display: flex;
      flex-direction: row;
    }
  }
}
</style>




// WEBPACK FOOTER //
// src/framework/primitives/AmfForm.vue